import { motion } from 'framer-motion'
import { useState } from 'react'
import { Calendar, TrendingUp, Brain, Zap, Globe, Building } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface TimelineEvent {
  year: string
  quarter: string
  title: string
  description: string
  penetration: number
  category: 'breakthrough' | 'adoption' | 'regulation' | 'investment'
  icon: React.ComponentType<any>
}

const AITimeline = () => {
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null)
  const { t } = useLanguage()

  const events: TimelineEvent[] = [
    {
      year: '2019',
      quarter: 'Q4',
      title: t('timeline.gpt2Release'),
      description: t('timeline.gpt2Description'),
      penetration: 5.2,
      category: 'breakthrough',
      icon: Brain
    },
    {
      year: '2020',
      quarter: 'Q2',
      title: t('timeline.covidAcceleration'),
      description: t('timeline.covidDescription'),
      penetration: 12.8,
      category: 'adoption',
      icon: TrendingUp
    },
    {
      year: '2021',
      quarter: 'Q1',
      title: t('timeline.enterpriseBoom'),
      description: t('timeline.enterpriseDescription'),
      penetration: 18.5,
      category: 'adoption',
      icon: Building
    },
    {
      year: '2022',
      quarter: 'Q4',
      title: t('timeline.chatgptLaunch'),
      description: t('timeline.chatgptDescription'),
      penetration: 28.3,
      category: 'breakthrough',
      icon: Zap
    },
    {
      year: '2023',
      quarter: 'Q2',
      title: t('timeline.investmentPeak'),
      description: t('timeline.investmentDescription'),
      penetration: 35.1,
      category: 'investment',
      icon: Globe
    },
    {
      year: '2024',
      quarter: 'Q4',
      title: t('timeline.currentState'),
      description: t('timeline.currentDescription'),
      penetration: 37.2,
      category: 'adoption',
      icon: TrendingUp
    }
  ]

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'breakthrough': return 'from-[#2c1810] to-[#2c1810]'
      case 'adoption': return 'from-[#2c1810] to-[#2c1810]'
      case 'regulation': return 'from-[#2c1810] to-[#2c1810]'
      case 'investment': return 'from-[#2c1810] to-[#2c1810]'
      default: return 'from-[#2c1810] to-[#2c1810]'
    }
  }

  const getCategoryBg = (category: string) => {
    switch (category) {
      case 'breakthrough': return 'bg-[#2c1810]/5 text-[#2c1810]'
      case 'adoption': return 'bg-[#2c1810]/5 text-[#2c1810]'
      case 'regulation': return 'bg-[#2c1810]/5 text-[#2c1810]'
      case 'investment': return 'bg-[#2c1810]/5 text-[#2c1810]'
      default: return 'bg-[#2c1810]/5 text-[#2c1810]'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-[#faf6f1]/80 border border-[#2c1810]/20 p-8"
    >
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-[#2c1810] mb-4">{t('timeline.title')}</h2>
        <p className="font-merriweather text-base sm:text-lg text-[#2c1810]/80 px-4">{t('timeline.subtitle')}</p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#2c1810]/20"></div>

        {/* Events */}
        <div className="space-y-8">
          {events.map((event, index) => (
            <motion.div
              key={`${event.year}-${event.quarter}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative flex items-start cursor-pointer group"
              onClick={() => setSelectedEvent(selectedEvent?.year === event.year ? null : event)}
            >
              {/* Icon */}
              <div className={`relative z-10 w-16 h-16 bg-gradient-to-r ${getCategoryColor(event.category)} rounded-none flex items-center justify-center border border-[#2c1810]/20 group-hover:scale-110 transition-transform`}>
                <event.icon className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <div className="ml-6 flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="font-playfair text-lg font-bold text-[#2c1810]">{event.year}</span>
                  <span className={`px-2 py-1 border border-[#2c1810]/20 text-xs font-medium ${getCategoryBg(event.category)}`}>
                    {t(`timeline.${event.category}`)}
                  </span>
                  <span className="font-merriweather text-sm text-[#2c1810]/60">{event.quarter}</span>
                </div>

                <h4 className="font-playfair text-lg font-semibold text-[#2c1810] mb-2 group-hover:text-[#2c1810]/80 transition-colors">
                  {event.title}
                </h4>
                
                <p className="font-merriweather text-[#2c1810]/80 mb-3">{event.description}</p>

                {/* Penetration meter */}
                <div className="flex items-center space-x-3">
                  <span className="font-merriweather text-sm text-[#2c1810]/60 min-w-fit">{t('timeline.aiPenetration')}:</span>
                  <div className="flex-1 bg-[#2c1810]/10 h-2 max-w-xs">
                    <motion.div
                      className={`h-2 bg-gradient-to-r ${getCategoryColor(event.category)}`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${event.penetration}%` }}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      viewport={{ once: true }}
                    />
                  </div>
                  <span className="font-merriweather text-sm font-semibold text-[#2c1810] min-w-fit">{event.penetration}%</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Future Projection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        viewport={{ once: true }}
        className="mt-12 p-6 bg-[#2c1810]/5 border border-[#2c1810]/20"
      >
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className="w-6 h-6 text-[#2c1810]" />
          <h4 className="font-playfair text-lg font-bold text-[#2c1810]">Future Projections</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="font-playfair text-2xl font-bold text-[#2c1810]">50%+</div>
            <div className="font-merriweather text-sm text-[#2c1810]/80">By 2025</div>
          </div>
          <div className="text-center">
            <div className="font-playfair text-2xl font-bold text-[#2c1810]">70%+</div>
            <div className="font-merriweather text-sm text-[#2c1810]/80">By 2027</div>
          </div>
          <div className="text-center">
            <div className="font-playfair text-2xl font-bold text-[#2c1810]">85%+</div>
            <div className="font-merriweather text-sm text-[#2c1810]/80">By 2030</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default AITimeline 